import { UserAccountDialogComponent } from "@/admin/user-account-dialog.component";
import { LayoutService } from "@/layout/service/layout.service";
import { AuthService, AuthUser } from "@/shared/auth.service";
import { DbQueryService } from "@/shared/db-query.service";
import { DialogBaseComponent } from "@/shared/dialog-base.component";
import { DialogWrapperService } from "@/shared/dialog-wrapper.service";
import { IMessage, MessageBusService, MessageTypeEnum } from "@/shared/message-bus";
import { CommonModule } from "@angular/common";
import { Component, computed, ElementRef, inject, ViewChild } from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import { GravatarQuickEditorCore } from "@gravatar-com/quick-editor";
import { sha256 } from "js-sha256";
import { AvatarModule } from "primeng/avatar";
import { BadgeModule } from "primeng/badge";
import { ButtonModule } from "primeng/button";
import { DialogService } from "primeng/dynamicdialog";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { OverlayBadgeModule } from "primeng/overlaybadge";
import { RippleModule } from "primeng/ripple";
import { StyleClassModule } from "primeng/styleclass";
import { PageTitleComponent } from "../page.title/page.title";
import { ConfirmDialog } from "primeng/confirmdialog";
import { ConfirmationService } from "primeng/api";

@Component({
  selector: "[app-topbar]",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    StyleClassModule,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    RippleModule,
    BadgeModule,
    OverlayBadgeModule,
    AvatarModule,
    PageTitleComponent,
    ConfirmDialog
  ],
  providers: [DialogService, ConfirmationService],
  templateUrl: "app.topbar.html",
})
export class AppTopbar {
  layoutService = inject(LayoutService);
  authService = inject(AuthService);
  dbQueryService = inject(DbQueryService);
  messageBusService = inject(MessageBusService);
  pngDialogService = inject(DialogService);
  dialogWrapperService = inject(DialogWrapperService);
  confirmationService = inject(ConfirmationService);
  router = inject(Router);

  newNotificationsCount = 0;
  authUser?: AuthUser;
  intervalHandle!: number;
  f = "mp";
  emHash = "";
  gravatarLink = "";
  gravatarEditor?: GravatarQuickEditorCore;

  isDarkTheme = computed(() => this.layoutService.isDarkTheme());

  @ViewChild("menubutton") menuButton!: ElementRef;

  async ngOnInit(): Promise<void> {
    const authUser = await this.authService.getLoggedInUser();
    this.authUser = authUser;

    if (this.authUser?.email) {
      const c = sha256(this.authUser?.email);
      this.emHash = c;
      this.gravatarLink = "https://www.gravatar.com/avatar/" + this.emHash + "?d=identicon";
    }

    this.messageBusService.messages.subscribe(this.checkMessageBus.bind(this));

    // check notifications immediately, then check every 5 minutes.
    this.intervalHandle = window.setInterval(this.checkIssues.bind(this), 1000 * 60 * 5);

    this.gravatarEditor = new GravatarQuickEditorCore({
      email: this.authUser?.email,
      scope: ["avatars"],
      onProfileUpdated: () => {
        // when profile is updated, update img url with timestamp so it reloads
        this.gravatarLink = this.gravatarLink.replace(/d=identicon.*?$/, "d=identicon&ts=" + new Date().getTime());
      },
      // onOpened: () => {
      //     console.log('Editor opened!');
      // },
    });
  }

  ngOnDestroy() {
    clearInterval(this.intervalHandle);
  }

  private async checkMessageBus(message: IMessage) {
    // TODO different behavior on different messages?
    if (message.id === MessageTypeEnum.Login) {
      await this.checkLockout();
      if (this.authUser?.isAccountingAdmin || this.authUser?.isSystemAdmin) {
        await this.testQuickbooks();
      }
      if (this.authUser?.isSystemAdmin) {
        await this.testProximityApi();
      }
    }
    if (message.id === MessageTypeEnum.Login || message.id === MessageTypeEnum.EntityIssuesChanged) {
      await this.checkIssues();
    }
  }

  private async checkIssues() {
    const count = await this.dbQueryService.getEntityIssuesCount();
    this.newNotificationsCount = count;
  }

  private async testQuickbooks() {
    const err = await this.dbQueryService.checkQuickbooksSetup();
    if (err) {
      await this.dialogWrapperService.okDialog( "QuickBooks", err, "topbar", this.confirmationService );
    }
  }

  private async testProximityApi() {
    const ver = await this.dbQueryService.getProximityApiVersion();
    if (ver.startsWith("Unable")) {
      await this.dialogWrapperService.okDialog( "Proximity API", ver, "topbar", this.confirmationService );
    }
  }

  private async checkLockout() {
    const configs = await this.dbQueryService.getConfigs("Lockout", "Schedule");
    if (configs.length === 0) { return; }
    const range = configs[0].value;
    if (!range) { return; }
    try {
      const dates = JSON.parse(range) as string[]; 
      if (dates.length != 2) { return; }
      const start = new Date(dates[0]);
      const end = new Date(dates[1]);
      const now = new Date();
      console.log(`Lockout schedule: ${start} - ${end}, now: ${now}`);
      if (now.getTime() >= start.getTime() && now.getTime() <= end.getTime()) {
          // In case the user keeps the page open and the lockout period ends, show a dialog when they interact with the page after the lockout period.
        await this.dialogWrapperService.okDialog(
          "Lockout",
          `The system is currently in a lockout period from ${start} to ${end}. Please try again later.`, "topbar", this.confirmationService
        );
        if (!this.authUser?.isSystemAdmin) {
          await this.authService.logout();
        }
      } else if (now.getTime() < start.getTime()) {
        await this.dialogWrapperService.okDialog(
          "Upcoming Lockout",
          `The system will be in a lockout period from ${start} to ${end}. Please plan accordingly.`, "topbar", this.confirmationService
        );
      }
    } catch (error) {
      console.error("Error parsing lockout schedule " + range, error);
    }
  }

  get userDisplayName(): string {
    const user = this.authService.getUser();
    return user ? user.displayName : "";
  }

  onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  showRightMenu() {
    this.layoutService.toggleRightMenu();
  }

  onConfigButtonClick() {
    this.layoutService.showConfigSidebar();
  }

  toggleSearchBar() {
    this.layoutService.layoutState.update((value) => ({ ...value, searchBarActive: !value.searchBarActive }));
  }

  setGravatar() {
    this.gravatarEditor?.open();
  }

  showProfile() {
    if (this.authUser) {
      return DialogBaseComponent.open(UserAccountDialogComponent, this, {
        userName: this.authUser.loginName,
      });
    }
  }

  // changePassword() {
  //     this.router.navigate(['/auth/change-password']);
  // }

  logout() {
    this.authService.logout();
  }

  // Stub methods for HTML parity with Proximity (functionality differs)
  showNotifications() {
    this.router.navigate(["/entity-issue"]);
  }

  changePassword() {
    // Uniformax handles password change through Profile dialog
    this.showProfile();
  }

  get saveChangesTooltip(): string | null {
    return null;
  }
}
